import { Field, Form, Formik } from 'formik';
import { Col, Row, Card, CardBody } from 'reactstrap';
import { FileText, Landmark, Send, AlertTriangle, Calculator } from 'lucide-react';

import useAuth from '../../../Hooks/useAuth';
import { Btn } from '../../../AbstractElements';
import { CustomDatePiker } from '../../../components/common/Form/CustomDatePiker';
import { FormikCurrencyInput } from '../../../components/ui/form/FormikCurrencyInput';
import { FormikInput } from '../../../components/ui/form/FormikInput';
import { FormikNumberInput } from '../../../components/ui/form/FormikNumberInput';
import { FormikSelect } from '../../../components/ui/form/FormikSelect';
import { loanRequestFormSchema } from '../validations/loanRequestFormSchema';
import { useCreateLoanRequest } from '../../../services/mutations/useCreateLoanRequest';
import { useLoanRequest } from '../../../Hooks/LoanRequest/useLoanRequest';
import { useNavigate } from 'react-router-dom';

export const LoanRequestForm = ({ onSimulateClick }: { onSimulateClick?: () => void }) => {
   const { user } = useAuth();
   const loanRequest = useLoanRequest();
   const createLoanRequest = useCreateLoanRequest();
   const navigate = useNavigate();

   const creditLimit = loanRequest.query.data?.credit_limit || 0;

   return (
      <Formik
         initialValues={loanRequest.selector.form}
         validationSchema={loanRequestFormSchema(creditLimit)}
         onSubmit={(values, { resetForm }) => {
            createLoanRequest.mutate(
               {
                  userId: user?.id!,
                  requestDate: values.disbursement!,
                  requestAmount: values.value!,
                  period: values.period!,
                  frequency: values.frequency!,
                  description: values.description,
                  bankId: +values.bank!,
                  accountNumber: +values.account!,
                  accountType: values.typeAccount!,
               },
               {
                  onSuccess: () => {
                     loanRequest.actions.setDefault();
                     resetForm();
                     navigate('/');
                  },
               }
            );
         }}
      >
         {({ handleSubmit, values }) => {
            // Update form values in Redux to update calculations reactively
            loanRequest.actions.setForm(values);

            const requestedValue = Number(values.value) || 0;
            const percentOfLimit = Math.min(
               creditLimit > 0 ? (requestedValue / creditLimit) * 100 : 0,
               100
            );
            const isOverLimit = requestedValue > creditLimit;

            return (
               <Card className="loan-card border-0 mb-0">
                  <div className="card-header-custom d-flex justify-content-between align-items-center">
                     <h5 className="mb-0">Formulario de solicitud</h5>
                     {onSimulateClick && (
                        <button
                           type="button"
                           className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1.5 py-1.5 px-3"
                           style={{ borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600 }}
                           onClick={onSimulateClick}
                        >
                           <Calculator size={14} />
                           <span>Simulación</span>
                        </button>
                     )}
                  </div>
                  <CardBody className="card-body-custom pt-0">
                     <Form onSubmit={handleSubmit}>
                        {/* Section 1: Loan Data */}
                        <div className="section-divider">
                           <span>Detalles del Préstamo</span>
                        </div>

                        {/* Interactive Credit Limit Progress Bar */}
                        {requestedValue > 0 && (
                           <div className="limit-progress-wrapper mb-4">
                              <div className="progress-labels">
                                 <span>Uso del Cupo Disponible</span>
                                 <span
                                    className={
                                       isOverLimit
                                          ? 'text-danger fw-bold'
                                          : 'text-muted'
                                    }
                                 >
                                    {isOverLimit
                                       ? '¡Excede el límite!'
                                       : `${percentOfLimit.toFixed(0)}%`}
                                 </span>
                              </div>
                              <div className="progress">
                                 <div
                                    className={`progress-bar ${
                                       isOverLimit
                                          ? 'bg-danger'
                                          : percentOfLimit > 85
                                          ? 'bg-warning'
                                          : 'bg-primary'
                                    }`}
                                    style={{ width: `${percentOfLimit}%` }}
                                 />
                              </div>
                              {isOverLimit && (
                                 <div className="d-flex align-items-center gap-1 mt-2 text-danger fs-12">
                                    <AlertTriangle size={14} />
                                    <span>
                                       El monto solicitado supera tu cupo disponible.
                                    </span>
                                 </div>
                              )}
                           </div>
                        )}

                        <Row className="gy-3">
                           <Col md={6} xs={12}>
                              <Field
                                 label="Fecha de desembolso"
                                 name="disbursement"
                                 placeholder="Desembolso"
                                 component={CustomDatePiker}
                                 minDate={new Date()}
                              />
                           </Col>
                           <Col md={6} xs={12}>
                              <FormikCurrencyInput
                                 name="value"
                                 label="Monto"
                                 placeholder="Ingresa el monto"
                                 className="rounded-lg"
                              />
                           </Col>
                        </Row>

                        <Row className="gy-3 mt-1">
                           <Col md={6} xs={12}>
                              <FormikSelect
                                 name="period"
                                 label="Periodo (Cuotas)"
                                 options={loanRequest.frequencyOptions}
                              />
                           </Col>
                           <Col md={6} xs={12}>
                              <FormikSelect
                                 name="frequency"
                                 label="Frecuencia"
                                 options={[
                                    { label: 'Quincenal', value: 'FORTNIGHTLY' },
                                    { label: 'Mensual', value: 'MONTHLY' },
                                 ]}
                              />
                           </Col>
                        </Row>

                        <Row className="mt-2">
                           <Col md={12}>
                              <FormikInput
                                 name="description"
                                 label="Información adicional"
                                 placeholder="Ingrese información adicional o propósito del préstamo"
                                 type="textarea"
                                 rows={3}
                              />
                           </Col>
                        </Row>

                        {/* Section 2: Disbursement Data */}
                        <div className="section-divider">
                           <span>Información de Desembolso</span>
                        </div>

                        <Row className="gy-3">
                           <Col md={6} xs={12}>
                              <FormikSelect
                                 name="bank"
                                 label="Banco"
                                 placeholder="Seleccione un banco"
                                 options={loanRequest.query.data?.banks || []}
                              />
                           </Col>

                           <Col md={6} xs={12}>
                              <FormikNumberInput
                                 name="account"
                                 label="Número de cuenta"
                                 type="bank-account"
                                 placeholder="Ingresa número de cuenta"
                                 className="rounded-lg"
                              />
                           </Col>
                        </Row>

                        <Row className="gy-3 mt-1">
                           <Col md={12}>
                              <FormikSelect
                                 name="typeAccount"
                                 label="Tipo de cuenta"
                                 placeholder="Seleccione un tipo de cuenta"
                                 options={[
                                    { value: 'AHORRO', label: 'Ahorro' },
                                    { value: 'CREDITO', label: 'Crédito' },
                                 ]}
                              />
                           </Col>
                        </Row>

                        <Row className="mt-4">
                           <Col md={12} className="text-end">
                              <Btn
                                 type="submit"
                                 color="primary"
                                 disabled={createLoanRequest.isPending}
                                 className="d-inline-flex align-items-center gap-2 px-4 py-2 text-white"
                              >
                                 <Send size={15} />
                                 {createLoanRequest.isPending
                                    ? 'Enviando...'
                                    : 'Solicitar préstamo'}
                              </Btn>
                           </Col>
                        </Row>
                     </Form>
                  </CardBody>
               </Card>
            );
         }}
      </Formik>
   );
};
